import { Alert, Button, Empty, Space, Table } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdOutlineAdd } from 'react-icons/md'

import { titlesOf } from '@/Containers/TransportationSection/Components'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { formatDateTimeUk, formatDateUk, renderUsersView } from '@/Ship/Helpers/fieldViews'
import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'

import { APPLICATION_FIELD_LABELS as L } from '../Contracts/ApplicationFieldLabels'
import { APPLICATION_DRAWER_WIDTH, APPLICATION_RESOURCE } from '../Contracts/IApplication'
import { openApplicationCreate } from '../Hooks/openApplicationCreate'
import { ApplicationOwnerListStore } from '../Stores/ApplicationOwnerListStore'
import { ApplicationLink, ApplicationStatus } from './ApplicationCells'

import type { ApplicationRecord } from '../Contracts/IApplication'
import type { TableColumnsType } from 'antd'
import type { ReactNode } from 'react'

/** Власник вкладки: значення форми клієнта чи перевізника. */
export interface ApplicationListOwner {
    uuid: string
    '@id'?: string
    title?: string
}

const PAGE_SIZE = 10

const dash = '—'

/** Колонки — як у реєстрі (легасі `ApplicationCrud` з префільтром власника), без колонки самого власника. */
const columnsFor = (ownerField: 'client' | 'carrier'): TableColumnsType<ApplicationRecord> =>
    [
        { title: L.id, key: 'id', dataIndex: 'id', width: 70 },
        ownerField === 'client'
            ? null
            : { title: L.client, key: 'client', render: (_: unknown, row: ApplicationRecord) => <ApplicationLink kind="client" record={row.client} /> },
        {
            title: L.status,
            key: 'status',
            render: (_: unknown, row: ApplicationRecord) => <ApplicationStatus status={row.status} countOffers={row.countOffers} />,
        },
        { title: L.loadingCountries, key: 'loadingCountries', render: (_: unknown, row: ApplicationRecord) => titlesOf(row.loadingCountries) || dash },
        { title: L.dateLoading, key: 'dateLoading', render: (_: unknown, row: ApplicationRecord) => formatDateUk(row.dateLoading) || dash },
        {
            title: L.unloadingCountries,
            key: 'unloadingCountries',
            render: (_: unknown, row: ApplicationRecord) => titlesOf(row.unloadingCountries) || dash,
        },
        ownerField === 'carrier'
            ? null
            : { title: L.carrier, key: 'carrier', render: (_: unknown, row: ApplicationRecord) => <ApplicationLink kind="carrier" record={row.carrier} /> },
        {
            title: L.manager,
            key: 'manager',
            render: (_: unknown, row: ApplicationRecord): ReactNode => (row.manager ? renderUsersView(row.manager) : dash),
        },
        {
            title: L.responsible,
            key: 'responsible',
            render: (_: unknown, row: ApplicationRecord): ReactNode => (row.responsible ? renderUsersView(row.responsible) : dash),
        },
        { title: L.createdAt, key: 'createdAt', render: (_: unknown, row: ApplicationRecord) => formatDateTimeUk(row.createdAt) || dash },
    ].filter((column): column is NonNullable<typeof column> => column !== null) as TableColumnsType<ApplicationRecord>

/**
 * Заявки клієнта чи перевізника в його картці (`SPEC-021` §8.9): список із
 * префільтром власника, без панелі фільтрів і канбану; «Додати заявку»
 * підставляє власника (у перевізника теж — легасі передавав
 * `initialValues.carrier` і кнопку не ховав, К25.9).
 *
 *  - **власна таблиця**, а не ядровий список: вкладений список чужого ресурсу
 *    підхоплює контекст картки, і збереження пішло б на адресу власника. Картку
 *    заявки відкриває `OpenResourceCardTask` — другою шухлядою поверх власника;
 *  - **«Додати заявку»** — за колекційним `create` заявок;
 *  - список перечитується після закриття картки заявки.
 */
export const ApplicationList = observer(
    ({ ownerField, owner }: { ownerField: 'client' | 'carrier'; owner: ApplicationListOwner }) => {
        const [store] = useState(() => new ApplicationOwnerListStore(ownerField, PAGE_SIZE))

        useEffect(() => {
            store.bind(owner.uuid)
        }, [store, owner.uuid])

        const reload = (): void => {
            void store.reload()
        }

        const ownerRef = {
            uuid: owner.uuid,
            '@id': owner['@id'] ?? `/services/${ownerField === 'client' ? 'clients' : 'carriers'}/${owner.uuid}`,
            ...(owner.title ? { title: owner.title } : {}),
        }

        const openCard = (uuid: string): void => {
            OpenResourceCardTask({
                resource: APPLICATION_RESOURCE,
                mode: 'view',
                displayMode: 'drawer',
                drawer: { width: APPLICATION_DRAWER_WIDTH },
                primaryFieldValue: uuid,
                onSave: async () => reload(),
                onClose: reload,
            })
        }

        const canAdd = CheckGlobalResourceAccessTask(APPLICATION_RESOURCE, 'create')

        return (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {canAdd ? (
                    <Button
                        type="primary"
                        icon={<MdOutlineAdd />}
                        onClick={() => openApplicationCreate({ source: ownerField, record: ownerRef, onSaved: reload, onClosed: reload })}
                    >
                        Додати заявку
                    </Button>
                ) : null}

                {store.failed ? <Alert type="error" showIcon title="Не вдалося завантажити заявки." /> : null}

                {!store.isLoading && store.items.length === 0 && !store.failed ? (
                    <Empty description="Заявок ще немає." />
                ) : (
                    <Table<ApplicationRecord>
                        size="small"
                        rowKey={(row) => String(row.uuid)}
                        columns={columnsFor(ownerField)}
                        dataSource={store.items}
                        loading={store.isLoading}
                        scroll={{ x: 'max-content' }}
                        pagination={
                            store.total > store.pageSize
                                ? {
                                      current: store.page,
                                      pageSize: store.pageSize,
                                      total: store.total,
                                      showSizeChanger: false,
                                      onChange: (page) => store.setPage(page),
                                  }
                                : false
                        }
                        onRow={(row) => ({
                            style: { cursor: 'pointer' },
                            onClick: () => {
                                if (typeof row.uuid === 'string') openCard(row.uuid)
                            },
                        })}
                    />
                )}
            </Space>
        )
    }
)
